import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, remove, update } from 'firebase/database';
import { FaPen, FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import Button from '../ui/Button';
import Card from '../ui/Card';
import {
  WorkspaceEmptyState,
  WorkspaceRail,
  WorkspaceSection,
  WorkspaceShell,
  WorkspacePageLoader,
  WorkspacePageError,
  WorkspaceSelectableList,
  WorkspaceStatusChip,
} from '../ui/Workspace';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../config/firebase';
import {
  useAdminCoursesOverview,
  useStudentCourses,
} from '../../hooks/useLmsData';
import {
  countDistinctBatchOfferings,
  getReadableEnrollmentStatus,
} from '../../utils/lmsSelectors';
import { renameCourseBatchAssignments } from '../../utils/lmsMutations';
import { lessonReaderUrlSegment, sortLessonsBySession } from '../../utils/lessonReader';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';

const CoursesPage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const adminResult = useAdminCoursesOverview({ enabled: isAdmin });
  const studentResult = useStudentCourses(currentUser?.uid, { enabled: !isAdmin });
  const { data, loading, error, refresh } = isAdmin ? adminResult : studentResult;
  const courses = useMemo(
    () => (isAdmin ? data?.courseList || [] : data?.allCourses || []),
    [data, isAdmin],
  );
  const {
    list: allBranches,
    activeList: activeBranches,
    byId: branchesById,
    bySlug: branchesBySlug,
  } = useBranches();
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [editingBatchIndex, setEditingBatchIndex] = useState(null);
  const [batchForm, setBatchForm] = useState(createBatchForm());
  const [batchError, setBatchError] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);

  const sortedCourses = useMemo(
    () => [...courses].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [courses],
  );

  const selectedCourse = useMemo(() => {
    const match = sortedCourses.find((course) => course.id === selectedCourseId);
    return match || sortedCourses[0] || null;
  }, [sortedCourses, selectedCourseId]);

  useEffect(() => {
    if (!sortedCourses.length) return;
    if (!sortedCourses.some((c) => c.id === selectedCourseId)) {
      setSelectedCourseId(sortedCourses[0].id);
    }
  }, [sortedCourses, selectedCourseId]);

  const batchBranchOptions = useMemo(() => {
    const branches = new Map();
    const addBranch = (branch) => {
      if (!branch?.name) return;
      const id = branch.id || branch.slug || branch.name;
      branches.set(id, {
        ...branch,
        id,
        slug: branch.slug || String(branch.name).toLowerCase().replace(/\s+/g, '-'),
      });
    };

    (allBranches?.length ? allBranches : activeBranches).forEach(addBranch);
    (selectedCourse?.batches || []).forEach((batch) => {
      const resolved = resolveBranchRef(batch, {
        byId: branchesById,
        bySlug: branchesBySlug,
      });
      addBranch(resolved);
    });

    return [...branches.values()].sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || '')),
    );
  }, [activeBranches, allBranches, branchesById, branchesBySlug, selectedCourse]);

  const groupedSeatSummary = useMemo(() => {
    const groups = new Map();
    (selectedCourse?.seatSummary || []).forEach((batch, index) => {
      const batchId = batch.batchId || `Batch ${index + 1}`;
      const groupKey = String(batchId);
      const branchRef = resolveBranchRef(batch, {
        byId: branchesById,
        bySlug: branchesBySlug,
      });
      const item = {
        ...batch,
        sourceIndex: index,
        branchLabel: branchRef?.name || batch.branchName || batch.branch || 'Branch not set',
      };

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          batchId,
          items: [],
        });
      }
      groups.get(groupKey).items.push(item);
    });

    return [...groups.values()].map((group) => {
      const enrolledCount = group.items.reduce(
        (total, item) => total + Number(item.enrolledCount || 0),
        0,
      );
      const maxStudents = group.items.reduce(
        (total, item) => total + Number(item.maxStudents || 0),
        0,
      );
      // Sum of per-branch max(0, cap − enrolled) is wrong when one branch is
      // over capacity; seats left is total capacity minus total enrollment.
      const availableSeats = Math.max(0, maxStudents - enrolledCount);
      const nextStart = group.items
        .map((item) => item.startDate)
        .filter(Boolean)
        .sort(
          (a, b) =>
            new Date(a.year, a.month - 1, a.date) -
            new Date(b.year, b.month - 1, b.date),
        )[0];

      return {
        ...group,
        primary: group.items[0],
        branchCount: group.items.length,
        enrolledCount,
        maxStudents,
        availableSeats,
        startDate: nextStart,
      };
    });
  }, [branchesById, branchesBySlug, selectedCourse]);

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    await remove(ref(db, `courses/${courseId}`));
    refresh();
  };

  const toggleCourseStatus = async (courseId, currentStatus) => {
    const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
    await update(ref(db, `courses/${courseId}`), { status: nextStatus });
    refresh();
  };

  const openAddBatch = () => {
    if (!selectedCourse) return;
    setEditingBatchIndex(null);
    setBatchError('');
    setBatchForm(
      createBatchForm({
        batchId: generateBatchId(selectedCourse.courseCode, selectedCourse.batches || []),
      }),
    );
    setIsBatchModalOpen(true);
  };

  const openEditBatchGroup = (group) => {
    const primary = selectedCourse.batches?.[group.primary.sourceIndex] || group.primary;
    setEditingBatchIndex(group.primary.sourceIndex);
    setBatchError('');
    setBatchForm({
      ...createBatchForm(primary),
      multiBranchEnabled: group.items.length > 1,
      branchSchedules: group.items.map((item) => {
        const batch = selectedCourse.batches?.[item.sourceIndex] || item;
        const branchRef = resolveBranchRef(batch, {
          byId: branchesById,
          bySlug: branchesBySlug,
        });
        return createBranchSchedule(
          {
            id: branchRef?.id || batch.branchId || batch.branch || batch.branchName || '',
            name: branchRef?.name || batch.branchName || batch.branch || '',
            slug: branchRef?.slug || batch.branch || '',
          },
          batch,
        );
      }),
    });
    setIsBatchModalOpen(true);
  };

  const closeBatchModal = () => {
    if (batchSaving) return;
    setIsBatchModalOpen(false);
    setEditingBatchIndex(null);
    setBatchError('');
    setBatchForm(createBatchForm());
  };

  const handleBatchFieldChange = (field, value) => {
    if (field === 'multiBranchEnabled') {
      setBatchForm((current) => {
        const selectedBranch =
          batchBranchOptions.find((branch) => branch.id === current.branchId) ||
          batchBranchOptions[0] ||
          null;
        return {
          ...current,
          multiBranchEnabled: value,
          branchSchedules:
            value && !(current.branchSchedules || []).length && selectedBranch
              ? [createBranchSchedule(selectedBranch, current)]
              : current.branchSchedules || [],
        };
      });
      return;
    }

    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setBatchForm((current) => ({
        ...current,
        [parent]: {
          ...current[parent],
          [child]: value,
        },
      }));
      return;
    }

    setBatchForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleBranchScheduleChange = (branchId, field, value) => {
    setBatchForm((current) => ({
      ...current,
      branchSchedules: (current.branchSchedules || []).map((item) => {
        if (item.branchId !== branchId) return item;
        if (field.includes('.')) {
          const [parent, child] = field.split('.');
          return {
            ...item,
            [parent]: {
              ...item[parent],
              [child]: value,
            },
          };
        }
        return {
          ...item,
          [field]: value,
        };
      }),
    }));
  };

  const toggleBranchForBatch = (branch) => {
    setBatchForm((current) => {
      const currentSchedules = current.branchSchedules || [];
      const exists = currentSchedules.some((item) => item.branchId === branch.id);
      if (exists) {
        return {
          ...current,
          branchSchedules: currentSchedules.filter((item) => item.branchId !== branch.id),
        };
      }

      return {
        ...current,
        branchSchedules: [
          ...currentSchedules,
          createBranchSchedule(branch, current),
        ],
      };
    });
  };

  const toggleBranchScheduleDay = (branchId, day) => {
    setBatchForm((current) => ({
      ...current,
      branchSchedules: (current.branchSchedules || []).map((item) => {
        if (item.branchId !== branchId) return item;
        const currentDays = item.schedule?.days || [];
        const nextDays = currentDays.includes(day)
          ? currentDays.filter((value) => value !== day)
          : [...currentDays, day];
        return {
          ...item,
          schedule: {
            ...item.schedule,
            days: nextDays,
          },
        };
      }),
    }));
  };

  const toggleBatchDay = (day) => {
    setBatchForm((current) => {
      const currentDays = current.schedule?.days || [];
      const nextDays = currentDays.includes(day)
        ? currentDays.filter((item) => item !== day)
        : [...currentDays, day];

      return {
        ...current,
        schedule: {
          ...current.schedule,
          days: nextDays,
        },
      };
    });
  };

  const branchesReferToSamePlace = (left = {}, right = {}) => {
    const leftTokens = [
      left.branchId,
      left.branch,
      left.branchName,
      resolveBranchRef(left, { byId: branchesById, bySlug: branchesBySlug })?.id,
      resolveBranchRef(left, { byId: branchesById, bySlug: branchesBySlug })?.slug,
      resolveBranchRef(left, { byId: branchesById, bySlug: branchesBySlug })?.name,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);
    const rightTokens = [
      right.branchId,
      right.branch,
      right.branchName,
      resolveBranchRef(right, { byId: branchesById, bySlug: branchesBySlug })?.id,
      resolveBranchRef(right, { byId: branchesById, bySlug: branchesBySlug })?.slug,
      resolveBranchRef(right, { byId: branchesById, bySlug: branchesBySlug })?.name,
    ]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean);

    return leftTokens.some((token) => rightTokens.includes(token));
  };

  const saveBatch = async () => {
    if (!selectedCourse) return;

    const previousBatch =
      editingBatchIndex == null ? null : selectedCourse.batches?.[editingBatchIndex];
    const normalizedBatches = batchForm.multiBranchEnabled
      ? (batchForm.branchSchedules || []).map((branchSchedule) =>
          normalizeBatch({
            ...batchForm,
            ...branchSchedule,
            batchId: batchForm.batchId,
          }),
        )
      : [normalizeBatch(batchForm)];

    if (!normalizedBatches.length) {
      setBatchError('Select at least one branch.');
      return;
    }

    if (normalizedBatches.some((item) => !item.schedule.days.length)) {
      setBatchError('Select at least one class day.');
      return;
    }

    setBatchSaving(true);
    setBatchError('');

    try {
      const nextBatches = Array.isArray(selectedCourse.batches)
        ? [...selectedCourse.batches]
        : [];

      const replacedBatches = [];

      if (editingBatchIndex == null) {
        normalizedBatches.forEach((batch) => nextBatches.push(batch));
      } else {
        normalizedBatches.forEach((batch, index) => {
          const existingIndex =
            index === 0
              ? editingBatchIndex
              : nextBatches.findIndex(
                  (item, itemIndex) =>
                    itemIndex !== editingBatchIndex &&
                    String(item.batchId) === String(previousBatch?.batchId || batch.batchId) &&
                    branchesReferToSamePlace(item, batch),
                );
          if (existingIndex >= 0) {
            const existingBatch = nextBatches[existingIndex];
            nextBatches[existingIndex] = {
              ...batch,
              enrolledStudents: Array.isArray(existingBatch?.enrolledStudents)
                ? existingBatch.enrolledStudents
                : [],
            };
            replacedBatches.push({ index: existingIndex, previous: existingBatch, current: batch });
          } else {
            nextBatches.push(batch);
          }
        });
      }

      await update(ref(db, `courses/${selectedCourse.id}`), {
        batches: nextBatches,
      });

      await Promise.all(
        replacedBatches
          .filter(
            ({ previous, current }) =>
              previous?.batchId &&
              current?.batchId &&
              previous.batchId !== current.batchId,
          )
          .map(({ index, previous, current }) =>
            renameCourseBatchAssignments({
              courseId: selectedCourse.id,
              batchIndex: index,
              oldBatchId: previous.batchId,
              newBatchId: current.batchId,
              branchId: current.branchId,
              branchName: current.branchName,
            }),
          ),
      );

      closeBatchModal();
      refresh();
    } catch (saveError) {
      console.error('Failed to save batch:', saveError);
      setBatchError('Could not save the batch. Please try again.');
    } finally {
      setBatchSaving(false);
    }
  };

  const handleDeleteBatchGroup = async (group) => {
    if (!selectedCourse) return;
    if (!window.confirm(`Delete ${group.batchId} and all branch schedules?`)) return;

    const indexesToRemove = new Set(group.items.map((item) => item.sourceIndex));
    const nextBatches = (selectedCourse.batches || []).filter(
      (_, index) => !indexesToRemove.has(index),
    );
    await update(ref(db, `courses/${selectedCourse.id}`), {
      batches: nextBatches,
    });
    refresh();
  };

  const getNextBatchDate = (batches = []) => {
    const nextBatch = batches[0];
    if (!nextBatch?.startDate) return 'Not scheduled';

    return new Date(
      nextBatch.startDate.year,
      nextBatch.startDate.month - 1,
      nextBatch.startDate.date,
    ).toLocaleDateString();
  };

  const stats = isAdmin
    ? {
        total: courses.length,
        active: courses.filter((course) => course.status === 'active').length,
        drafts: courses.filter((course) => course.status !== 'active').length,
        missingLessons: courses.filter(
          (course) => !Array.isArray(course.lessons) || course.lessons.length === 0,
        ).length,
      }
    : {
        total: courses.length,
        active: courses.filter((course) => course.status === 'active').length,
        waiting: courses.filter((course) =>
          ['request_submitted', 'payment_pending', 'payment_review'].includes(
            course.status,
          ),
        ).length,
        available: courses.filter((course) => course.status === 'available').length,
      };

  if (loading) {
    return <WorkspacePageLoader />;
  }

  if (error) {
    return (
      <WorkspacePageError>
        {error.message || 'Failed to load courses'}
      </WorkspacePageError>
    );
  }

  return (
    <WorkspaceShell
      title={isAdmin ? 'Course Operations' : 'Course Discovery'}
      description={
        isAdmin
          ? 'Compact workflow: choose a course, review readiness, then run batch and publishing actions.'
          : 'Pick a course from the left rail to review status, batches, and next steps.'
      }
      actions={
        isAdmin ? (
          <Button onClick={() => navigate('/courses/add')}>Add New Course</Button>
        ) : (
          <Button variant="outline" onClick={() => navigate('/courses/my-courses')}>
            Open My Courses
          </Button>
        )
      }
    >
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(250px,300px)_minmax(0,1fr)] xl:items-start">
        <aside className="min-w-0 space-y-4 xl:sticky xl:top-4 xl:self-start">
          <div className="grid grid-cols-2 gap-2">
            <CompactStatTile label="Total" value={stats.total} />
            <CompactStatTile
              label={isAdmin ? 'Published' : 'Active'}
              value={stats.active}
              status="active"
            />
            <CompactStatTile
              label={isAdmin ? 'Drafts' : 'Waiting'}
              value={isAdmin ? stats.drafts : stats.waiting}
              status={isAdmin ? 'draft' : 'pending'}
            />
            <CompactStatTile
              label={isAdmin ? 'No Lessons' : 'Available'}
              value={isAdmin ? stats.missingLessons : stats.available}
              status={isAdmin ? 'attention' : 'planning'}
            />
          </div>

          <WorkspaceRail
            title="Course Navigator"
            description={
              isAdmin
                ? 'Select a course to open its operations workspace.'
                : 'Select a course to open your enrollment and schedule view.'
            }
          >
            <WorkspaceSelectableList
              items={sortedCourses}
              selectedId={selectedCourse?.id || ''}
              onSelect={setSelectedCourseId}
              getKey={(course) => course.id}
              emptyTitle="No courses available"
              emptyMessage="Add your first course to begin building lessons and batches."
              renderItem={(course, selected) => (
                <CourseNavigatorItem
                  course={course}
                  isSelected={selected}
                  isAdmin={isAdmin}
                />
              )}
            />
          </WorkspaceRail>
        </aside>

        <div className="min-w-0 space-y-4">
          {!selectedCourse ? (
            <WorkspaceEmptyState
              title="No course selected"
              message="Choose a course from the navigator on the left."
            />
          ) : (
            <>
              <Card className="shadow-sm">
                <div className="space-y-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                        Selected course
                      </p>
                      <h2 className="mt-1 truncate text-xl font-semibold tracking-tight text-stone-950">
                        {selectedCourse.name || 'Untitled course'}
                      </h2>
                      <p className="mt-1 text-sm text-stone-600">
                        {selectedCourse.courseCode || 'No code'} ·{' '}
                        {selectedCourse.academicLevel || 'Level not set'} ·{' '}
                        {selectedCourse.activeEnrollmentCount || 0} active students
                      </p>
                    </div>
                    <CompactStatusPill
                      status={isAdmin ? selectedCourse.status || 'draft' : selectedCourse.status}
                      label={
                        isAdmin
                          ? selectedCourse.status || 'draft'
                          : getReadableEnrollmentStatus(selectedCourse.status).label
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
                    <Metric label="Type" value={selectedCourse.type || 'Course'} />
                    <Metric
                      label="Duration"
                      value={
                        selectedCourse.duration?.isOneDay
                          ? 'One day'
                          : `${selectedCourse.duration?.months || 0} months`
                      }
                    />
                    <Metric label="Fee" value={`Rs. ${selectedCourse.fees?.fullCourseFee || 0}`} />
                    <Metric
                      label="Next batch"
                      value={getNextBatchDate(selectedCourse.batches)}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/courses/${selectedCourse.id}`)}
                    >
                      Open Details
                    </Button>
                    {isAdmin &&
                    Array.isArray(selectedCourse.lessons) &&
                    selectedCourse.lessons.length > 0 ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const list = selectedCourse.lessons;
                          const first = sortLessonsBySession(list)[0];
                          const seg = lessonReaderUrlSegment(list, first);
                          navigate(`/courses/${selectedCourse.id}/lessons/${seg}`);
                        }}
                      >
                        Open first lesson
                      </Button>
                    ) : null}
                    {isAdmin ? (
                      <Button
                        size="sm"
                        onClick={() =>
                          toggleCourseStatus(selectedCourse.id, selectedCourse.status)
                        }
                      >
                        {selectedCourse.status === 'active' ? 'Unpublish' : 'Publish'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate(
                            selectedCourse.status === 'active'
                              ? `/courses/my-courses/${selectedCourse.id}`
                              : selectedCourse.status === 'payment_pending' ||
                                selectedCourse.status === 'payment_review'
                              ? '/payments'
                              : `/courses/${selectedCourse.id}`,
                          )
                        }
                      >
                        {selectedCourse.status === 'active'
                          ? 'Continue Learning'
                          : selectedCourse.status === 'request_submitted'
                          ? 'Review Request'
                          : selectedCourse.status === 'payment_pending' ||
                            selectedCourse.status === 'payment_review'
                          ? 'Open Payments'
                          : 'View Details'}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>

          {isAdmin ? (
            <>
              <WorkspaceSection
                title="Publishing And Lesson Readiness"
                description="Keep the course visible only when its delivery pieces are ready."
                status={
                  Array.isArray(selectedCourse.lessons) &&
                  selectedCourse.lessons.length > 0
                    ? 'healthy'
                    : 'attention'
                }
                statusLabel={
                  Array.isArray(selectedCourse.lessons) &&
                  selectedCourse.lessons.length > 0
                    ? 'ready'
                    : 'needs lessons'
                }
                action={
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/courses/edit/${selectedCourse.id}`)}
                    >
                      Edit Course
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/lesson-planner')}
                    >
                      Open Lesson Planner
                    </Button>
                  </div>
                }
              >
                <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                  <Metric
                    label="Lessons"
                    value={selectedCourse.lessons?.length || 0}
                  />
                  <Metric
                    label="Batches"
                    value={countDistinctBatchOfferings(selectedCourse.batches)}
                  />
                  <Metric
                    label="Assigned Instructors"
                    value={selectedCourse.instructors?.length || 0}
                  />
                  <Metric
                    label="Enrollment Health"
                    value={`${selectedCourse.activeEnrollmentCount || 0} active`}
                  />
                </div>
              </WorkspaceSection>

              <WorkspaceSection
                title="Batch Operations"
                description="Watch capacity and jump into enrollment work when a batch needs help."
                status={
                  groupedSeatSummary.some(
                    (batch) => batch.maxStudents && batch.availableSeats <= 2,
                  )
                    ? 'urgent'
                    : 'healthy'
                }
                statusLabel={
                  groupedSeatSummary.some(
                    (batch) => batch.maxStudents && batch.availableSeats <= 2,
                  )
                    ? 'near capacity'
                    : 'healthy'
                }
                action={
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={openAddBatch}>
                      <span className="inline-flex items-center gap-2">
                        <FaPlus className="text-xs" />
                        Add Batch
                      </span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate('/enrollments')}
                    >
                      Open Enrollment Queue
                    </Button>
                  </div>
                }
              >
                {groupedSeatSummary.length ? (
                  <BatchOperationsTable
                    batches={groupedSeatSummary}
                    getNextBatchDate={getNextBatchDate}
                    onEdit={openEditBatchGroup}
                    onDelete={handleDeleteBatchGroup}
                  />
                ) : (
                  <WorkspaceEmptyState
                    title="No batches configured"
                    message="Add a batch before publishing this course to students."
                  />
                )}
                <div className="hidden">
                  {groupedSeatSummary.map((batch) => (
                    <div key={batch.batchId} className="rounded-lg border border-stone-200 p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-stone-900">
                            {batch.batchId}
                          </p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {batch.branchCount > 1
                              ? `1 batch · ${batch.branchCount} branches`
                              : batch.primary.branchLabel}
                          </p>
                        </div>
                        <WorkspaceStatusChip
                          status={
                            batch.maxStudents && batch.availableSeats <= 2
                              ? 'urgent'
                              : 'healthy'
                          }
                          label={`${batch.availableSeats} seats left`}
                        />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-2">
                        <Metric
                          label="Enrolled"
                          value={`${batch.enrolledCount}/${batch.maxStudents || 0}`}
                        />
                        <Metric
                          label="Start"
                          value={getNextBatchDate([batch])}
                        />
                      </div>
                      {batch.branchCount > 1 ? (
                        <div className="mt-3 space-y-1.5">
                          {batch.items.map((item) => (
                            <div
                              key={`${batch.batchId}-${item.sourceIndex}`}
                              className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-xs"
                            >
                              <span className="font-medium text-stone-700">
                                {item.branchLabel}
                              </span>
                              <span className="text-stone-500">
                                {item.enrolledCount}/{item.maxStudents || 0} enrolled
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : null}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEditBatchGroup(batch)}
                        >
                          <span className="inline-flex items-center gap-2">
                            <FaPen className="text-xs" />
                            Edit
                          </span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteBatchGroup(batch)}
                        >
                          <span className="inline-flex items-center gap-2 text-red-700">
                            <FaTrash className="text-xs" />
                            Delete
                          </span>
                        </Button>
                      </div>
                    </div>
                  ))}
                  {!groupedSeatSummary.length && (
                    <WorkspaceEmptyState
                      title="No batches configured"
                      message="Add a batch before publishing this course to students."
                    />
                  )}
                </div>
              </WorkspaceSection>

              <WorkspaceSection
                title="Lifecycle Actions"
                description="Keep destructive actions separate from the main workflow."
                status="planning"
              >
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/courses/edit/${selectedCourse.id}`)}
                  >
                    Edit Course Settings
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeleteCourse(selectedCourse.id)}
                  >
                    Delete Course
                  </Button>
                </div>
              </WorkspaceSection>
            </>
          ) : (
            <>
              <WorkspaceSection
                title="Available Batches"
                description="Open the course detail page to choose your batch and complete the request."
                status={
                  selectedCourse.batches?.length ? 'planning' : 'blocked'
                }
                statusLabel={
                  selectedCourse.batches?.length ? 'ready' : 'not scheduled'
                }
                action={
                  <Button
                    size="sm"
                    onClick={() => navigate(`/courses/${selectedCourse.id}`)}
                  >
                    Open Enrollment Flow
                  </Button>
                }
              >
                {selectedCourse.batches?.length ? (
                  <AvailableBatchesTable
                    batches={selectedCourse.batches || []}
                    course={selectedCourse}
                    branchesById={branchesById}
                    branchesBySlug={branchesBySlug}
                    getNextBatchDate={getNextBatchDate}
                  />
                ) : (
                  <WorkspaceEmptyState
                    title="No batches open yet"
                    message="Check back soon or contact the academy team for the next intake."
                  />
                )}
              </WorkspaceSection>
            </>
          )}
            </>
          )}
        </div>
      </div>

      {isAdmin && isBatchModalOpen ? (
        <BatchEditorModal
          batch={batchForm}
          error={batchError}
          saving={batchSaving}
          isEditing={editingBatchIndex != null}
          onClose={closeBatchModal}
          onChange={handleBatchFieldChange}
          onBranchScheduleChange={handleBranchScheduleChange}
          onToggleBranch={toggleBranchForBatch}
          onToggleDay={toggleBatchDay}
          onToggleBranchDay={toggleBranchScheduleDay}
          onSave={saveBatch}
          branches={batchBranchOptions}
          branchesById={branchesById}
        />
      ) : null}
    </WorkspaceShell>
  );
};

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-stone-50 px-3 py-2.5">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">
      {label}
    </p>
    <p className="mt-0.5 text-sm font-semibold text-stone-900">{value}</p>
  </div>
);

const CompactStatTile = ({ label, value, status }) => (
  <div className="rounded-lg border border-stone-200 bg-white px-3 py-2.5">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
        {label}
      </p>
      {status ? <CompactStatusDot status={status} /> : null}
    </div>
    <p className="mt-1 text-xl font-semibold tracking-tight text-stone-950">{value}</p>
  </div>
);

const BatchOperationsTable = ({ batches, getNextBatchDate, onEdit, onDelete }) => (
  <div className="overflow-x-auto rounded-md border border-stone-200">
    <table className="min-w-full border-separate border-spacing-0 text-sm">
      <thead>
        <tr>
          {['Batch', 'Branches', 'Enrolled', 'Seats left', 'Start', 'Status', 'Actions'].map((heading) => (
            <th
              key={heading}
              className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {batches.map((batch) => (
          <tr key={batch.batchId} className="group hover:bg-stone-50">
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 font-semibold text-stone-950 group-hover:bg-stone-50">
              {batch.batchId}
            </td>
            <td className="border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">
              {batch.branchCount > 1
                ? batch.items.map((item) => item.branchLabel).join(', ')
                : batch.primary.branchLabel}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">
              {batch.enrolledCount}/{batch.maxStudents || 0}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">
              {batch.availableSeats}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700 group-hover:bg-stone-50">
              {getNextBatchDate([batch])}
            </td>
            <td className="whitespace-nowrap px-3 py-2">
              <WorkspaceStatusChip
                status={batch.maxStudents && batch.availableSeats <= 2 ? 'urgent' : 'healthy'}
                label={batch.maxStudents && batch.availableSeats <= 2 ? 'near capacity' : 'healthy'}
              />
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 group-hover:bg-stone-50">
              <div className="flex gap-3">
                <button type="button" onClick={() => onEdit(batch)} className="text-xs font-semibold text-[#b91c66] hover:text-[#9d174d]">Edit</button>
                <button type="button" onClick={() => onDelete(batch)} className="text-xs font-semibold text-red-600 hover:text-red-900">Delete</button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AvailableBatchesTable = ({
  batches,
  course,
  branchesById,
  branchesBySlug,
  getNextBatchDate,
}) => (
  <div className="overflow-x-auto rounded-md border border-stone-200">
    <table className="min-w-full border-separate border-spacing-0 text-sm">
      <thead>
        <tr>
          {['Batch', 'Branch', 'Start', 'Capacity', 'Mode'].map((heading) => (
            <th
              key={heading}
              className="border-b border-stone-200 bg-stone-50 px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-100 bg-white">
        {batches.map((batch, index) => (
          <tr key={batch.batchId || index} className="hover:bg-stone-50">
            <td className="whitespace-nowrap px-3 py-2 font-semibold text-stone-950">
              {batch.batchId || `Batch ${index + 1}`}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-stone-700">
              {resolveBranchRef(batch, {
                byId: branchesById,
                bySlug: branchesBySlug,
              })?.name || 'Branch not set'}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700">
              {getNextBatchDate([batch])}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700">
              {batch.maxStudents || 'Open'}
            </td>
            <td className="whitespace-nowrap border-b border-stone-100 px-3 py-2 text-stone-700">
              {batch.mode || course.type || 'Course'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CourseNavigatorItem = ({ course, isSelected, isAdmin }) => {
  const statusLabel = isAdmin
    ? course.status || 'draft'
    : getReadableEnrollmentStatus(course.status).label;

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-stone-900">
            {course.name || 'Untitled course'}
          </p>
          <p className="truncate text-xs text-stone-500">
            {course.courseCode || 'No code'} · {course.academicLevel || 'Level not set'}
          </p>
        </div>
        <CompactStatusPill status={course.status || 'draft'} label={statusLabel} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs text-stone-600">
        <span>{course.lessons?.length || 0} lessons</span>
        <span>{countDistinctBatchOfferings(course.batches)} batches</span>
      </div>
      {isSelected ? (
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-[#b91c66]">
          Selected
        </p>
      ) : null}
    </div>
  );
};

const CompactStatusDot = ({ status }) => (
  <span
    className={`inline-flex h-2.5 w-2.5 rounded-full ${getCompactStatusClasses(status)}`}
    aria-label={status}
  />
);

const CompactStatusPill = ({ status, label }) => (
  <span
    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium capitalize ${getCompactStatusClasses(
      status,
      true,
    )}`}
  >
    {label || status || 'not started'}
  </span>
);

const getCompactStatusClasses = (status, textMode = false) => {
  switch (status) {
    case 'active':
    case 'approved':
    case 'completed':
    case 'covered':
    case 'healthy':
      return textMode ? 'bg-emerald-50 text-emerald-700' : 'bg-emerald-500';
    case 'pending':
    case 'request_submitted':
    case 'payment_pending':
    case 'payment_review':
    case 'attention':
    case 'urgent':
      return textMode ? 'bg-amber-50 text-amber-700' : 'bg-amber-500';
    case 'draft':
    case 'planning':
    case 'in_progress':
      return textMode ? 'bg-pink-50 text-pink-700' : 'bg-pink-500';
    case 'inactive':
    case 'blocked':
    case 'unassigned':
      return textMode ? 'bg-stone-100 text-stone-700' : 'bg-stone-500';
    case 'declined':
    case 'failed':
      return textMode ? 'bg-red-50 text-red-700' : 'bg-red-500';
    default:
      return textMode ? 'bg-stone-100 text-stone-700' : 'bg-stone-500';
  }
};

const SCHEDULE_DAY_OPTIONS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const DEFAULT_BATCH_SCHEDULE = {
  days: ['MON'],
  timeSlot: '09:00-12:00',
  lessonsPerWeek: 1,
  notes: '',
};

function normalizeSchedule(schedule = {}) {
  return {
    ...DEFAULT_BATCH_SCHEDULE,
    ...(schedule || {}),
    days: Array.isArray(schedule?.days) && schedule.days.length
      ? schedule.days
      : DEFAULT_BATCH_SCHEDULE.days,
  };
}

function createBranchSchedule(branch = {}, source = {}) {
  return {
    branchId: branch.id || source.branchId || '',
    branchName: branch.name || source.branchName || '',
    branch: branch.slug || source.branch || branch.name?.toLowerCase() || '',
    startDate: {
      year: Number(source.startDate?.year || new Date().getFullYear()),
      month: Number(source.startDate?.month || new Date().getMonth() + 1),
      date: Number(source.startDate?.date || new Date().getDate()),
    },
    maxStudents: Number(source.maxStudents || 20),
    schedule: normalizeSchedule(source.schedule),
  };
}

function createBatchForm(batch = {}) {
  const today = new Date();
  const primaryBranch = {
    id: batch.branchId || '',
    name: batch.branchName || '',
    slug: batch.branch || '',
  };
  return {
    batchId: batch.batchId || '',
    startDate: {
      year: Number(batch.startDate?.year || today.getFullYear()),
      month: Number(batch.startDate?.month || today.getMonth() + 1),
      date: Number(batch.startDate?.date || today.getDate()),
    },
    branchId: batch.branchId || '',
    branchName: batch.branchName || '',
    branch: batch.branch || 'colombo',
    maxStudents: Number(batch.maxStudents || 20),
    schedule: normalizeSchedule(batch.schedule),
    multiBranchEnabled: false,
    branchSchedules: batch.branchId
      ? [createBranchSchedule(primaryBranch, batch)]
      : [],
  };
}

function normalizeBatch(batch) {
  return {
    batchId: batch.batchId || '',
    startDate: {
      year: Number(batch.startDate?.year || 0),
      month: Number(batch.startDate?.month || 0),
      date: Number(batch.startDate?.date || 0),
    },
    branchId: batch.branchId || '',
    branchName: batch.branchName || '',
    branch: batch.branch || 'colombo',
    maxStudents: Number(batch.maxStudents || 0),
    schedule: {
      days: Array.isArray(batch.schedule?.days) ? batch.schedule.days : [],
      timeSlot: batch.schedule?.timeSlot || '',
      lessonsPerWeek: Number(batch.schedule?.lessonsPerWeek || 1),
      notes: batch.schedule?.notes || '',
    },
  };
}

function generateBatchId(courseCode, existingBatches = []) {
  const today = new Date();
  const year = String(today.getFullYear()).slice(-2);
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const prefix = courseCode || 'COURSE';

  const nextSequence = (
    Math.max(
      0,
      ...existingBatches
        .map((batch) => parseInt(String(batch.batchId || '').slice(-2), 10))
        .filter((value) => !Number.isNaN(value)),
    ) + 1
  )
    .toString()
    .padStart(2, '0');

  return `${prefix}-${year}${month}-${nextSequence}`;
}

const BatchEditorModal = ({
  batch,
  error,
  saving,
  isEditing,
  onClose,
  onChange,
  onBranchScheduleChange,
  onToggleBranch,
  onToggleDay,
  onToggleBranchDay,
  onSave,
  branches = [],
  branchesById = {},
}) => (
  <div
    className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/45 p-3 sm:items-center"
    onClick={onClose}
  >
    <div
      className="flex max-h-[92vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex items-start justify-between gap-4 border-b border-stone-200 px-4 py-3 sm:px-5">
        <div>
          <h3 className="text-lg font-semibold text-stone-950">
            {isEditing ? 'Edit Batch' : 'Add New Batch'}
          </h3>
          <p className="mt-0.5 text-xs text-stone-500 sm:text-sm">
            Update batch schedule, branch, and capacity without opening the full
            course editor.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-stone-200 text-stone-500 transition hover:bg-stone-50 hover:text-stone-700"
          aria-label="Close batch editor"
        >
          <FaTimes className="h-4 w-4" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
        {error ? (
          <div className="mb-4 rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        ) : null}

      <div className="space-y-4">
        <div className={`grid gap-3 ${batch.multiBranchEnabled ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]' : 'md:grid-cols-3'}`}>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Batch ID</label>
            <input
              type="text"
              value={batch.batchId}
              onChange={(event) => onChange('batchId', event.target.value)}
              className="ledger-input mt-1 min-h-[38px]"
            />
          </div>
          {batch.multiBranchEnabled ? (
            <div className="rounded-xl border border-pink-100 bg-pink-50/60 px-3 py-2">
              <p className="text-sm font-medium text-pink-900">
                {(batch.branchSchedules || []).length} branch schedule(s)
              </p>
              <p className="mt-1 text-xs text-pink-800">
                Total capacity:{' '}
                {(batch.branchSchedules || []).reduce(
                  (total, item) => total + Number(item.maxStudents || 0),
                  0,
                )}{' '}
                students. Branch and capacity are edited per branch below.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                  Branch
                </label>
                <select
                  value={
                    batch.branchId && branchesById[batch.branchId]
                      ? batch.branchId
                      : ''
                  }
                  onChange={(event) => {
                    const selected =
                      branchesById[event.target.value] ||
                      branches.find((branch) => branch.id === event.target.value);
                    if (!selected) return;
                    onChange('branchId', selected.id);
                    onChange('branchName', selected.name);
                    onChange(
                      'branch',
                      selected.slug || selected.name.toLowerCase(),
                    );
                  }}
                  className="ledger-input mt-1 min-h-[38px]"
                >
                  {branches.length === 0 ? (
                    <option value="">No branches configured</option>
                  ) : null}
                  {!batch.branchId && branches.length ? (
                    <option value="">Select a branch...</option>
                  ) : null}
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                  {batch.branchId && !branchesById[batch.branchId] ? (
                    <option value={batch.branchId}>
                      {batch.branchName || batch.branch || 'Legacy branch'}
                    </option>
                  ) : null}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                  Maximum Students
                </label>
                <input
                  type="number"
                  min="1"
                  value={batch.maxStudents}
                  onChange={(event) => onChange('maxStudents', event.target.value)}
                  className="ledger-input mt-1 min-h-[38px]"
                />
              </div>
            </>
          )}
        </div>

        <label className="flex items-center gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2.5 text-sm font-medium text-stone-800">
          <input
            type="checkbox"
            checked={Boolean(batch.multiBranchEnabled)}
            onChange={(event) => onChange('multiBranchEnabled', event.target.checked)}
            className="h-4 w-4 rounded border-stone-300 text-pink-600 focus:ring-pink-500"
          />
          Use multiple branches for this batch
        </label>

        {batch.multiBranchEnabled ? (
          <div className="space-y-3">
            <div>
              <p className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Branches</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {branches.map((branch) => {
                  const selected = (batch.branchSchedules || []).some(
                    (item) => item.branchId === branch.id,
                  );
                  return (
                    <button
                      key={branch.id}
                      type="button"
                      onClick={() => onToggleBranch(branch)}
                      className={`rounded-md border px-3 py-1 text-sm font-medium ${
                        selected
                          ? 'border-pink-500 bg-pink-50 text-pink-800'
                          : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                      }`}
                    >
                      {branch.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid gap-3 xl:grid-cols-2">
              {(batch.branchSchedules || []).map((branchSchedule) => (
                <div
                  key={branchSchedule.branchId}
                  className="rounded-lg border border-stone-200 bg-white p-3"
                >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold text-stone-900">
                    {branchSchedule.branchName || branchSchedule.branch || 'Branch'}
                  </p>
                  <button
                    type="button"
                    onClick={() =>
                      onToggleBranch({
                        id: branchSchedule.branchId,
                        name: branchSchedule.branchName,
                        slug: branchSchedule.branch,
                      })
                    }
                    className="text-xs font-semibold text-red-600 hover:text-red-800"
                  >
                    Remove
                  </button>
                </div>

                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                      Start Date
                    </label>
                    <div className="mt-1 grid grid-cols-[minmax(4.5rem,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5">
                      <input
                        type="number"
                        value={branchSchedule.startDate.year}
                        onChange={(event) =>
                          onBranchScheduleChange(
                            branchSchedule.branchId,
                            'startDate.year',
                            event.target.value,
                          )
                        }
                        placeholder="Year"
                        min="1900"
                        max="9999"
                        className="ledger-input min-w-0 tabular-nums min-h-[38px]"
                      />
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={branchSchedule.startDate.month}
                        onChange={(event) =>
                          onBranchScheduleChange(
                            branchSchedule.branchId,
                            'startDate.month',
                            event.target.value,
                          )
                        }
                        placeholder="Month"
                        className="ledger-input min-h-[38px]"
                      />
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={branchSchedule.startDate.date}
                        onChange={(event) =>
                          onBranchScheduleChange(
                            branchSchedule.branchId,
                            'startDate.date',
                            event.target.value,
                          )
                        }
                        placeholder="Date"
                        className="ledger-input min-h-[38px]"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                      Time Slot
                    </label>
                    <input
                      type="text"
                      value={branchSchedule.schedule.timeSlot}
                      onChange={(event) =>
                        onBranchScheduleChange(
                          branchSchedule.branchId,
                          'schedule.timeSlot',
                          event.target.value,
                        )
                      }
                      placeholder="09:00-12:00"
                      className="ledger-input mt-1 min-h-[38px]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                      Maximum Students
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={branchSchedule.maxStudents}
                      onChange={(event) =>
                        onBranchScheduleChange(
                          branchSchedule.branchId,
                          'maxStudents',
                          event.target.value,
                        )
                      }
                      className="ledger-input mt-1 min-h-[38px]"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                      Lessons Per Week
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={branchSchedule.schedule.lessonsPerWeek}
                      onChange={(event) =>
                        onBranchScheduleChange(
                          branchSchedule.branchId,
                          'schedule.lessonsPerWeek',
                          event.target.value,
                        )
                      }
                      className="ledger-input mt-1 min-h-[38px]"
                    />
                  </div>
                </div>

                <div className="mt-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                      Class Days
                    </label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {SCHEDULE_DAY_OPTIONS.map((day) => {
                        const isSelected = branchSchedule.schedule.days.includes(day);
                        return (
                          <button
                            key={day}
                            type="button"
                            onClick={() =>
                              onToggleBranchDay(branchSchedule.branchId, day)
                            }
                            className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                              isSelected
                                ? 'border-pink-500 bg-pink-50 text-pink-800'
                                : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                            }`}
                          >
                            {day}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                    Schedule Notes
                  </label>
                  <textarea
                    rows="2"
                    value={branchSchedule.schedule.notes}
                    onChange={(event) =>
                      onBranchScheduleChange(
                        branchSchedule.branchId,
                        'schedule.notes',
                        event.target.value,
                      )
                    }
                    placeholder="Optional notes, holidays, or timetable constraints"
                    className="ledger-input mt-1 min-h-[38px]"
                  />
                </div>
              </div>
              ))}
            </div>
          </div>
        ) : (
          <>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Start Date</label>
            <div className="mt-1 grid grid-cols-[minmax(4.5rem,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-1.5">
              <input
                type="number"
                value={batch.startDate.year}
                onChange={(event) => onChange('startDate.year', event.target.value)}
                placeholder="Year"
                min="1900"
                max="9999"
                className="ledger-input min-w-0 tabular-nums min-h-[38px]"
              />
              <input
                type="number"
                min="1"
                max="12"
                value={batch.startDate.month}
                onChange={(event) => onChange('startDate.month', event.target.value)}
                placeholder="Month"
                className="ledger-input min-h-[38px]"
              />
              <input
                type="number"
                min="1"
                max="31"
                value={batch.startDate.date}
                onChange={(event) => onChange('startDate.date', event.target.value)}
                placeholder="Date"
                className="ledger-input min-h-[38px]"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Time Slot</label>
            <input
              type="text"
              value={batch.schedule.timeSlot}
              onChange={(event) => onChange('schedule.timeSlot', event.target.value)}
              placeholder="09:00-12:00"
              className="ledger-input mt-1 min-h-[38px]"
            />
          </div>
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
              Lessons Per Week
            </label>
            <input
              type="number"
              min="1"
              value={batch.schedule.lessonsPerWeek}
              onChange={(event) =>
                onChange('schedule.lessonsPerWeek', event.target.value)
              }
              className="ledger-input mt-1 min-h-[38px]"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">Class Days</label>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {SCHEDULE_DAY_OPTIONS.map((day) => {
              const isSelected = batch.schedule.days.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => onToggleDay(day)}
                  className={`rounded-md border px-2.5 py-1 text-xs font-medium ${
                    isSelected
                      ? 'border-pink-500 bg-pink-50 text-pink-800'
                      : 'border-stone-200 text-stone-600 hover:bg-stone-50'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Schedule Notes
          </label>
          <textarea
            rows="3"
            value={batch.schedule.notes}
            onChange={(event) => onChange('schedule.notes', event.target.value)}
            placeholder="Optional notes, holidays, or timetable constraints"
            className="ledger-input mt-1 min-h-[38px]"
          />
        </div>
          </>
        )}
      </div>
      </div>

      <div className="flex flex-wrap justify-end gap-3 border-t border-gray-100 px-4 py-3 sm:px-5">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save Batch' : 'Add Batch'}
        </Button>
      </div>
    </div>
  </div>
);

export default CoursesPage;
